<script setup lang="ts">
import SubMenuDesktopLayout from '~/components/App/NewHeader/Layouts/DesktopMenu/index.vue'
import HeaderMain from '~/components/App/NewHeader/HeaderMain/index.vue'
import HeaderSide from '~/components/App/NewHeader/HeaderSide/index.vue'
import Icon from '~/components/Icon/Legacy/index.vue'
import ReadMore from '~/components/App/NewHeader/ReadMode/index.vue'

interface Props {
  alt?: boolean
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const itemsHeaderSide: InstanceType<typeof HeaderSide>['$props']['items'] = [
  {
    title: 'Snippets',
    text: 'Simplify your AI workflow with snippets',
    imageUrl: '/images/LucasHire/202111e6fa0dcd149b1ebc6844431ad1.webp',
    url: '/join?section=snippets',
    handler: () => emit('close'),
  },
  {
    title: 'Teamspaces',
    text: 'A workspace for each team that you work with',
    imageUrl: '/images/LucasHire/e1adcc9e094e4c1872c693d5f45f77a1.webp',
    url: '/join?section=team-specific-dashboards',
    handler: () => emit('close'),
  },
  {
    title: 'Create Prompts',
    text: 'Create the best prompts for each of your tasks',
    imageUrl: '/images/LucasHire/521080099680a05b26bf19908e3d4033.png',
    url: '/join?section=create-share-discover',
    handler: () => emit('close'),
  },
]

const openMenuState = useHeaderMenusActive()
const isOpen = computed(() => openMenuState.value.desktopApp)
</script>

<template>
  <SubMenuDesktopLayout :active="isOpen" :alt="props?.alt">
    <template #main>
      <HeaderMain
        subtitle="Snack Prompt Desktop"
        title="The All-in-One AI Platform for Teams"
        description="We consolidate LLMs into a user-friendly dashboard, simplifying AI  adoption and boosting productivity for individuals & teams"
      >
        <template #image>
          <img src="~/assets/images/LucasHire/ff4727b7c92306c9e72ad54aa50136aa.webp" rounded-10px>
        </template>

        <template #subtitleIcon>
          <div class="subtitle-icon">
            <IconLogo w-14px />
          </div>
        </template>

        <template #buttons>
          <div class="desktop-menu-container-buttons">
            <NuxtLink to="/join" class="button-main" @click="emit('close')">
              Get Started Now

              <Icon name="windows" class="icon" />
              <Icon name="apple" class="icon" />
              <Icon name="linux" class="icon" />
            </NuxtLink>

            <ReadMore href="/join" />
          </div>
        </template>

        <template #contentBottom>
          <div class="content-bottom-text">
            <p>Compatible with</p>
          </div>

          <div class="content-bottom-items">
            <div class="content-bottom-item-desktop-menu">
              <Icon name="chatgpt-logo" w-80px />
              <Icon name="perplexity-logo" w-100px />
            </div>
            <div class="content-bottom-item-desktop-menu">
              <Icon name="gemini-logo" w-60px />
              <Icon name="bing-ai-logo" w-70px />
            </div>
            <div class="content-bottom-item-desktop-menu">
              <Icon name="claude-logo" w-60px />
              <Icon name="dalle-logo" w-60px />
            </div>
            <div class="content-bottom-item-desktop-menu">
              <Icon name="midjourney-logo" w-110px />
              <p>
                AND MORE COMING...
              </p>
            </div>
          </div>
        </template>
      </HeaderMain>
    </template>

    <template #aside>
      <HeaderSide
        type="big"
        title="Features"
        :items="itemsHeaderSide"
      />
    </template>
  </SubMenuDesktopLayout>
</template>

<style scoped lang="scss">
.desktop-menu-container-buttons {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  .button-main {
    --alpha: 0.20;
    --at-apply: text-purple;

    max-width: 308px;
    padding: 8px 30px;
    margin: 0 28px 0 0;
    border-radius: 999px;
    font-family: 'Circular Std Bold';
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    background:
      linear-gradient(250deg,
        rgba(53, 219, 255, var(--alpha)) -2.27%,
        rgba(176, 158, 248, var(--alpha)) 43.11%,
        rgba(253, 147, 231, var(--alpha)) 100.75%);

    &:hover {
      --alpha: 0.50;
      cursor: pointer;
    }

    svg {
      --at-apply: fill-purple;
      width: 15px;
      height: 15px;
      min-width: 15px;
    }
  }
}

.subtitle-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: linear-gradient(250deg, rgba(53, 219, 255, 0.20) -2.27%, rgba(176, 158, 248, 0.20) 43.11%, rgba(253, 147, 231, 0.20) 100.75%);
}

.content-bottom-text {
  --at-apply: text-bluish-darker;
  min-width: 290px;
  font-family: 'Inter';
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: 'cv01' on, 'cv09' on;
  line-height: 175%;
  --at-apply: text-bluish-darker;
}
.content-bottom-items {
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px 40px;

  .content-bottom-item-desktop-menu {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: calc(100% / 6);

    svg:nth-child(1) {
      margin: 0 0 20px 0;
    }

    p {
      --at-apply: text-bluish-darker;
      font-family: 'Circular Std Bold';
      font-size: 10px;
      line-height: 9.238px;
      white-space: nowrap;
    }
  }
}

@media screen and (max-width: 1200px) {
  .desktop-menu-container-buttons {
    display: flex;

    .button-main {
      padding: 8px 20px;
      margin: 0 20px 0 0;
      font-family: 'Circular Std Bold';
      font-size: 12px;
      gap: 5px;

      svg {
        width: 12px;
        height: 12px;
        min-width: 12px;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .desktop-menu-container-buttons {
    display: flex;

    .button-main {
      padding: 7px 17px;
      margin: 0;
      font-family: 'Circular Std Bold';
      font-size: 12px;
      gap: 5px;

      svg {
        width: 12px;
        height: 12px;
        min-width: 12px;
      }
    }
  }
}
</style>
